import {
  chmodSync,
  existsSync,
  mkdirSync,
  readFileSync,
  writeFileSync,
} from "node:fs";
import { delimiter, dirname, join, resolve } from "node:path";
import { daemonRuntimeRootPath } from "../config/daemon-state";
import {
  resolveWakeLoopEnvPreset,
  type WakeLoopPathResolutionInput,
} from "../config/paths";
import {
  DISPATCH_OWNED_RUNTIME_MODE,
  WAKELOOP_DISPATCH_RUNTIME_DISPATCH_ID_ENV,
  WAKELOOP_DISPATCH_RUNTIME_MODE_ENV,
  WAKELOOP_DISPATCH_RUNTIME_TARGET_PROFILE_ID_ENV,
  WAKELOOP_USER_FACING_CLI_COMMAND_ENV,
} from "./dispatch-runtime-env";
import { WAKELOOP_SPACE_ACTION_CONTEXT_PATH_ENV } from "./dispatch-space-action-context";
import {
  resolveWakeLoopDistribution,
  type WakeLoopDistribution,
} from "./distribution";

const DISPATCH_OWNED_RUNTIME_CLI_SHIM_DIRNAME = "dispatch-owned-cli";
const DISPATCH_SPACE_ACTION_CLI_SHIM_DIRNAME = "bin";
const WAKELOOP_ENV_PRESET_ENV = "WAKELOOP_ENV_PRESET";
const DISPATCH_OWNED_RUNTIME_CLI_COMMAND_NAMES = [
  "wakeloop",
  "wakeloop-dev",
  "wakeloop-canary",
] as const;

export type DispatchOwnedRuntimeGuard = {
  active: boolean;
  dispatchId: string | null;
  targetProfileId: string | null;
};

export function buildDispatchOwnedRuntimeEnv(input: {
  dispatchId: string;
  targetProfileId: string;
  distribution?: WakeLoopDistribution;
  env?: Record<string, string | undefined>;
  cliEntryPath?: string;
  spaceActionContextPath?: string | null;
}): Record<string, string | undefined> {
  const currentPath = normalizeOptionalText(
    input.env?.PATH ?? process.env.PATH
  );
  const shimDir = ensureDispatchOwnedRuntimeCliShims({
    cliEntryPath: input.cliEntryPath,
    dispatchId: input.dispatchId,
    distribution: input.distribution,
    env: input.env,
    spaceActionContextPath: input.spaceActionContextPath,
    targetProfileId: input.targetProfileId,
  });
  return {
    ...(input.env ?? {}),
    PATH: prependPathEntry({
      currentPath,
      nextEntry: shimDir,
    }),
    [WAKELOOP_DISPATCH_RUNTIME_MODE_ENV]: DISPATCH_OWNED_RUNTIME_MODE,
    [WAKELOOP_DISPATCH_RUNTIME_DISPATCH_ID_ENV]: input.dispatchId,
    [WAKELOOP_DISPATCH_RUNTIME_TARGET_PROFILE_ID_ENV]: input.targetProfileId,
    [WAKELOOP_USER_FACING_CLI_COMMAND_ENV]:
      resolveDispatchOwnedRuntimeActionCliEntryCommand({
        env: input.env,
      }),
    [WAKELOOP_ENV_PRESET_ENV]: resolveDispatchOwnedRuntimeEnvPreset({
      env: input.env,
    }),
    ...(input.spaceActionContextPath
      ? {
          [WAKELOOP_SPACE_ACTION_CONTEXT_PATH_ENV]:
            input.spaceActionContextPath,
        }
      : {}),
  };
}

export function resolveDispatchOwnedRuntimeActionCliEntryCommand(
  input: WakeLoopPathResolutionInput = {}
): "wakeloop" | "wakeloop-dev" | "wakeloop-canary" {
  const preset = resolveWakeLoopEnvPreset(input);
  if (preset === "dev-local") {
    return "wakeloop-dev";
  }
  if (preset === "canary") {
    return "wakeloop-canary";
  }
  return "wakeloop";
}

export function resolveDispatchOwnedRuntimeCliShimDir(
  input: WakeLoopPathResolutionInput & {
    spaceActionContextPath?: string | null;
  } = {}
): string {
  const spaceActionContextPath = normalizeOptionalText(
    input.spaceActionContextPath
  );
  if (spaceActionContextPath) {
    return join(
      dirname(spaceActionContextPath),
      DISPATCH_SPACE_ACTION_CLI_SHIM_DIRNAME
    );
  }
  return join(
    daemonRuntimeRootPath(input),
    "bin",
    DISPATCH_OWNED_RUNTIME_CLI_SHIM_DIRNAME
  );
}

export function ensureDispatchOwnedRuntimeCliShims(input?: {
  cliEntryPath?: string;
  distribution?: WakeLoopDistribution;
  dispatchId?: string;
  env?: Record<string, string | undefined>;
  spaceActionContextPath?: string | null;
  targetProfileId?: string;
}): string {
  const distribution = input?.distribution ?? resolveWakeLoopDistribution();
  const cliEntryPath =
    distribution === "binary"
      ? resolveDispatchOwnedRuntimeBinaryCliEntryPath(input?.cliEntryPath)
      : resolveDispatchOwnedRuntimeCliEntryPath(input?.cliEntryPath);
  const shimDir = resolveDispatchOwnedRuntimeCliShimDir({
    env: input?.env,
    spaceActionContextPath: input?.spaceActionContextPath,
  });
  mkdirSync(shimDir, { recursive: true });

  for (const commandName of DISPATCH_OWNED_RUNTIME_CLI_COMMAND_NAMES) {
    const shimPath = join(shimDir, commandName);
    const shimSource = buildDispatchOwnedRuntimeCliShimSource({
      cliEntryPath,
      commandName,
      dispatchId: input?.dispatchId,
      distribution,
      nodePath: process.execPath,
      spaceActionContextPath: input?.spaceActionContextPath,
      targetProfileId: input?.targetProfileId,
    });
    if (existsSync(shimPath)) {
      const currentSource = safeReadTextFile(shimPath);
      if (currentSource === shimSource) {
        chmodSync(shimPath, 0o755);
        continue;
      }
    }
    writeFileSync(shimPath, shimSource, {
      encoding: "utf8",
      mode: 0o755,
    });
    chmodSync(shimPath, 0o755);
  }

  return shimDir;
}

export function readDispatchOwnedRuntimeGuard(
  env: NodeJS.ProcessEnv = process.env
): DispatchOwnedRuntimeGuard {
  const mode = normalizeOptionalText(env[WAKELOOP_DISPATCH_RUNTIME_MODE_ENV]);
  if (mode !== DISPATCH_OWNED_RUNTIME_MODE) {
    return {
      active: false,
      dispatchId: null,
      targetProfileId: null,
    };
  }

  return {
    active: true,
    dispatchId: normalizeOptionalText(
      env[WAKELOOP_DISPATCH_RUNTIME_DISPATCH_ID_ENV]
    ),
    targetProfileId: normalizeOptionalText(
      env[WAKELOOP_DISPATCH_RUNTIME_TARGET_PROFILE_ID_ENV]
    ),
  };
}

export function assertDispatchOwnedRuntimePublicWriteAllowed(input: {
  commandLabel: string;
  env?: NodeJS.ProcessEnv;
}): void {
  const guard = readDispatchOwnedRuntimeGuard(input.env);
  if (!guard.active) {
    return;
  }

  const dispatchIdSuffix = guard.dispatchId
    ? ` (dispatch ${guard.dispatchId})`
    : "";
  throw new Error(
    `${input.commandLabel} is blocked inside a dispatch-owned runtime${dispatchIdSuffix}. Public space writes must be returned through WakeLoop Space structured actions: post_message, post_status, or stay_silent.`
  );
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function resolveDispatchOwnedRuntimeCliEntryPath(
  cliEntryPath?: string
): string {
  const normalizedCliEntryPath =
    normalizeOptionalText(cliEntryPath) ??
    normalizeOptionalText(process.argv[1]) ??
    null;
  if (!normalizedCliEntryPath) {
    throw new Error(
      "dispatch-owned runtime CLI shim requires a current CLI entry path"
    );
  }
  return resolve(normalizedCliEntryPath);
}

function resolveDispatchOwnedRuntimeBinaryCliEntryPath(
  cliEntryPath?: string
): string {
  const normalizedCliEntryPath =
    normalizeOptionalText(cliEntryPath) ??
    normalizeOptionalText(process.execPath) ??
    null;
  if (!normalizedCliEntryPath) {
    throw new Error(
      "dispatch-owned runtime CLI shim requires a current binary CLI path"
    );
  }
  return resolve(normalizedCliEntryPath);
}

function buildDispatchOwnedRuntimeCliShimSource(input: {
  cliEntryPath: string;
  commandName: (typeof DISPATCH_OWNED_RUNTIME_CLI_COMMAND_NAMES)[number];
  distribution: WakeLoopDistribution;
  dispatchId?: string;
  nodePath: string;
  spaceActionContextPath?: string | null;
  targetProfileId?: string;
}): string {
  const embeddedEnv = {
    [WAKELOOP_DISPATCH_RUNTIME_MODE_ENV]: DISPATCH_OWNED_RUNTIME_MODE,
    [WAKELOOP_ENV_PRESET_ENV]: resolveEnvPresetForCommandName(
      input.commandName
    ),
    [WAKELOOP_USER_FACING_CLI_COMMAND_ENV]: input.commandName,
    ...(normalizeOptionalText(input.dispatchId)
      ? {
          [WAKELOOP_DISPATCH_RUNTIME_DISPATCH_ID_ENV]: normalizeOptionalText(
            input.dispatchId
          ) as string,
        }
      : {}),
    ...(normalizeOptionalText(input.targetProfileId)
      ? {
          [WAKELOOP_DISPATCH_RUNTIME_TARGET_PROFILE_ID_ENV]:
            normalizeOptionalText(input.targetProfileId) as string,
        }
      : {}),
    ...(normalizeOptionalText(input.spaceActionContextPath)
      ? {
          [WAKELOOP_SPACE_ACTION_CONTEXT_PATH_ENV]: normalizeOptionalText(
            input.spaceActionContextPath
          ) as string,
        }
      : {}),
  };
  if (input.distribution === "binary") {
    const exportRows = Object.entries(embeddedEnv).map(
      ([key, value]) => `export ${key}=${quoteShellValue(value)}`
    );
    return `#!/bin/sh
${exportRows.join("\n")}
exec ${quoteShellValue(input.cliEntryPath)} "$@"
`;
  }
  return `#!/usr/bin/env node
import { spawnSync } from "node:child_process";

const result = spawnSync(${JSON.stringify(input.nodePath)}, [${JSON.stringify(
    input.cliEntryPath
  )}, ...process.argv.slice(2)], {
  env: { ...process.env, ...${JSON.stringify(embeddedEnv)} },
  stdio: "inherit",
});

if (result.error) {
  throw result.error;
}

process.exit(result.status ?? 1);
`;
}

function resolveDispatchOwnedRuntimeEnvPreset(input: {
  env?: Record<string, string | undefined>;
}): "prod" | "dev-local" | "canary" {
  return resolveEnvPresetForCommandName(
    resolveDispatchOwnedRuntimeActionCliEntryCommand({
      env: input.env,
    })
  );
}

function resolveEnvPresetForCommandName(
  commandName: (typeof DISPATCH_OWNED_RUNTIME_CLI_COMMAND_NAMES)[number]
): "prod" | "dev-local" | "canary" {
  if (commandName === "wakeloop-dev") {
    return "dev-local";
  }
  if (commandName === "wakeloop-canary") {
    return "canary";
  }
  return "prod";
}

function quoteShellValue(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

function safeReadTextFile(path: string): string | null {
  try {
    return readFileSync(path, "utf8");
  } catch {
    return null;
  }
}

function prependPathEntry(input: {
  currentPath: string | null;
  nextEntry: string;
}): string {
  const currentEntries = (input.currentPath ?? "")
    .split(delimiter)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  const dedupedEntries = currentEntries.filter(
    (entry) => entry !== input.nextEntry
  );
  return [input.nextEntry, ...dedupedEntries].join(delimiter);
}
